/**
 * What a settings field accepts, checked before anything is sent.
 *
 * Each rule answers null for a value it accepts, or one sentence saying what
 * is expected. The service applies the same rules (`routes/fields.py`); these
 * only answer sooner. An empty value is accepted everywhere: empty clears.
 *
 * The sentence never contains the value. It may be a key in the wrong field.
 */

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const NAMED_EMAIL = /^[^<>]*<\s*([^@\s<>]+@[^@\s<>]+\.[^@\s<>]+)\s*>$/
const HOST = /^[A-Za-z0-9]([A-Za-z0-9.-]*[A-Za-z0-9])?$/

export function isUrl(value: string): boolean {
  if (!value || /\s/.test(value)) return false
  let parsed: URL
  try {
    parsed = new URL(value)
  } catch {
    return false
  }
  return (parsed.protocol === 'http:' || parsed.protocol === 'https:') && parsed.host !== ''
}

/** `keyField` names where a key goes, for a URL field with one beside it. */
export function urlProblem(value: string, keyField?: string): string | null {
  const typed = value.trim()
  if (!typed || isUrl(typed)) return null
  return keyField
    ? `Enter a URL starting with http:// or https://. A key goes in the ${keyField} field.`
    : 'Enter a URL starting with http:// or https://.'
}

/** A bearer token is the token alone. The service adds the word itself. */
export function tokenProblem(value: string): string | null {
  return /^\s*bearer\s/i.test(value) ? 'Enter the token only, without the word Bearer.' : null
}

export function hostProblem(value: string): string | null {
  const typed = value.trim()
  if (!typed || HOST.test(typed)) return null
  return 'Enter a host name, without http:// and without a port.'
}

export function portProblem(value: string): string | null {
  const typed = value.trim()
  if (!typed) return null
  if (/^\d+$/.test(typed) && Number(typed) >= 1 && Number(typed) <= 65535) return null
  return 'Enter a number from 1 to 65535.'
}

export function emailProblem(value: string): string | null {
  const typed = value.trim()
  if (!typed || EMAIL.test(typed)) return null
  return 'Enter an email address.'
}

/** A bare address, or a display name followed by the address in angle brackets. */
export function senderProblem(value: string): string | null {
  const typed = value.trim()
  if (!typed || EMAIL.test(typed) || NAMED_EMAIL.test(typed)) return null
  return 'Enter an email address, with or without a display name.'
}

export function noSpacesProblem(value: string): string | null {
  return /\s/.test(value.trim()) ? 'Remove the spaces.' : null
}

/** Comma separated email addresses. */
export function recipientsProblem(value: string): string | null {
  const entries = value
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
  return entries.every((entry) => EMAIL.test(entry))
    ? null
    : 'Enter email addresses separated by commas.'
}

export function daysProblem(value: string): string | null {
  const typed = value.trim()
  if (/^\d+$/.test(typed) && Number(typed) >= 1 && Number(typed) <= 3650) return null
  return 'Enter a whole number of days from 1 to 3650.'
}
